import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { doc, getDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import i18n from '../config/i18n';
import { supportedLanguages } from '../supportedLanguages';
import {
  isValidShareId,
  linkifyReferences,
  translationLabel,
} from '../utils/shared-note';

const APP_STORE_URL = 'https://apps.apple.com/fr/app/bible4me/id6743235720';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.bible4md.app';

interface SharedNote {
  title: string | null;
  content: string;
  translation: string | null;
}

/**
 * La page qu'atteint un lien `bible4.me/import/<id>` (#361).
 *
 * Elle n'est **pas** le chemin nominal : quand l'application est installée, ce
 * sont les App Links et les Universal Links qui l'ouvrent, et le navigateur ne
 * vient jamais ici. Cette page est ce que voit celui qui n'a pas encore
 * l'application — d'où la note lisible telle quelle, et l'invitation à
 * l'installer pour la recevoir vraiment.
 *
 * La lecture est **anonyme** : `shared_notes/{id}` est ouvert à qui possède le
 * lien, comme le commentaire du jour l'est à qui connaît sa date. Un document
 * échu est refusé par la règle Firestore, donc indistinguable d'un lien
 * inconnu — et c'est voulu : l'écran n'a qu'un message à dire.
 */
const ImportNote: React.FC = () => {
  const { shareId } = useParams();
  const { t } = useTranslation();
  const [note, setNote] = useState<SharedNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Aucun préfixe de langue dans l'URL — c'est la condition pour n'avoir qu'un
  // seul chemin à déclarer aux App Links. La langue vient donc du navigateur.
  const lang = useMemo(() => {
    const code = (navigator.language || 'fr').split('-')[0].toLowerCase();
    return supportedLanguages.includes(code) ? code : 'fr';
  }, []);

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [lang]);

  useEffect(() => {
    if (!isValidShareId(shareId)) {
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    getDoc(doc(collection(db, 'shared_notes'), shareId))
      .then(snap => {
        const data = snap.data();
        if (!snap.exists() || !data || !data.content) {
          setError(true);
          setLoading(false);
          return;
        }
        setNote({
          title: data.title ?? null,
          content: data.content as string,
          translation: data.translation ?? null,
        });
        setLoading(false);
      })
      .catch(() => {
        // Une note échue est refusée par la règle, pas absente : même message.
        setError(true);
        setLoading(false);
      });
  }, [shareId]);

  const body = useMemo(
    () => (note ? linkifyReferences(note.content, lang, note.translation) : ''),
    [note, lang],
  );
  const bible = translationLabel(note?.translation);
  const heading = note?.title || t('sharedNote.untitled');

  return (
    <>
      <Helmet>
        <title>{`${heading} — Bible4Me`}</title>
        {/* Rien à indexer : le contenu est privé au lien, et il disparaît au
            bout de trente jours. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-amber-100 text-amber-900 pb-32">
        <div className="sticky top-0 z-20 bg-amber-200 px-4 py-3 shadow-sm">
          <a
            href={`/${lang}`}
            className="text-amber-900 font-bold text-sm hover:opacity-70 transition-opacity"
          >
            Bible4Me
          </a>
        </div>

        <div className="mx-auto max-w-2xl px-4 py-8">
          {loading && (
            <div className="text-center text-sm text-amber-700">
              {t('common.loading')}
            </div>
          )}

          {error && (
            <div className="rounded-2xl bg-amber-50 px-5 py-8 text-center shadow-sm">
              <p className="font-semibold">{t('sharedNote.notFound')}</p>
              <p className="mt-2 text-sm text-amber-700">
                {t('sharedNote.notFoundHint')}
              </p>
            </div>
          )}

          {note && (
            <>
              <p className="text-xs uppercase tracking-wider text-amber-700">
                {t('sharedNote.eyebrow')}
              </p>
              <h1 className="mt-1 text-2xl font-bold">{heading}</h1>
              {bible && (
                <p className="mt-1 text-sm text-amber-700">
                  {t('sharedNote.bible', { name: bible })}
                </p>
              )}
              <article className="prose prose-amber mt-6 max-w-none rounded-2xl bg-amber-50 px-5 py-6 shadow-sm">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
              </article>
              <p className="mt-4 text-center text-xs text-amber-700">
                {t('sharedNote.expiry')}
              </p>
            </>
          )}
        </div>

        {/* L'installation est la seule action utile ici : celui qui a déjà
            l'application n'a pas vu cette page, son système l'a ouverte. */}
        <div className="fixed bottom-0 inset-x-0 z-30 bg-amber-900 px-4 py-3 shadow-lg">
          <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wider text-amber-200">
            {t('sharedNote.cta')}
          </p>
          <div className="mx-auto flex max-w-2xl gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-black px-3 py-2 text-white transition-colors hover:bg-gray-900"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.38.07 2.34.74 3.16.8 1.2-.24 2.35-.93 3.63-.84 1.54.12 2.7.72 3.46 1.83-3.17 1.9-2.42 6.05.7 7.22-.62 1.5-1.44 2.99-2.95 3.87zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              <div className="leading-tight">
                <div className="text-[0.6rem] text-gray-300">App Store</div>
                <div className="text-sm font-bold">iPhone / iPad</div>
              </div>
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-black px-3 py-2 text-white transition-colors hover:bg-gray-900"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3.18 1.47A1.5 1.5 0 001.5 3v18a1.5 1.5 0 001.68 1.53L14.25 12 3.18 1.47z" fill="#4FC3F7" />
                <path d="M17.96 9.3L14.25 12l3.71 2.7 3.35-1.93a1.5 1.5 0 000-2.6L17.96 9.3z" fill="#FFD54F" />
                <path d="M14.25 12L3.18 22.53c.17.16.38.28.62.33l12.5-7.16L14.25 12z" fill="#F06292" />
                <path d="M14.25 12L3.8 1.14c-.24.05-.45.17-.62.33L14.25 12z" fill="#81C784" />
              </svg>
              <div className="leading-tight">
                <div className="text-[0.6rem] text-gray-300">Google Play</div>
                <div className="text-sm font-bold">Android</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImportNote;
